import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import * as signalR from '@microsoft/signalr';
import { SignalType } from 'angular-to-phaser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Channel, Friendship, NotificationSignal, RandomEventNotification, Signal, SpotlightSignal, User } from '../dto';
import { AbstractService } from './abstractservice';
import { AuthService } from './auth.service';
import { FeatureFlagService } from './feature-flag.service';
import { JsonEnrichmentService } from './jsonmapper.service';
import { getSessionName, withSessionName } from './telemetry/session-name';
@Injectable({
    providedIn: 'root'
})
export class SignalService extends AbstractService {

    // "The most recent notification received," not a meaningful standing value - every consumer
    // (AchievementService, MessageboardService, InboxService, RandomEventService,
    // SpotlightService) reacts to each arrival via .subscribe(), none reads a current snapshot.
    // Left as RxJS per the current-value-vs-event-stream rule of thumb, same as
    // GameDataService.scoreSubmitted$.
    public latestNotificationSignal: BehaviorSubject<Signal> = new BehaviorSubject<Signal>(null);

    private readonly toastsState: WritableSignal<ToastNotification[]> = signal<ToastNotification[]>([]);
    public readonly toasts: Observable<ToastNotification[]> = toObservable(this.toastsState);

    // Written by addAnnouncement/removeAnnouncement but not read anywhere else in the app
    // (verified by grep) - AnnouncementComponent takes an AnnouncementNotification via a direct
    // initialize() call, not a subscription to this list. No Observable bridge needed.
    private readonly announcementsState: WritableSignal<AnnouncementNotification[]> = signal<AnnouncementNotification[]>([]);

    private connection: signalR.HubConnection;

    // Fires once the hub connection is usable - the very first successful connect, and again
    // every time SignalR's own automatic reconnect recovers from a dropped connection. Generic
    // hook point for anything that needs to (re-)sync once we're back online; SystemNotificationService's
    // fetch-and-advance is the only current subscriber.
    private readonly reconnectedSubject = new Subject<void>();
    public readonly reconnected$: Observable<void> = this.reconnectedSubject.asObservable();

    constructor(authService: AuthService, featureFlagService: FeatureFlagService, private http: HttpClient, private jsonMapperService: JsonEnrichmentService) {
        super(authService, featureFlagService);
    }

    public async initializeConnection(): Promise<void> {
        if (!this.connection) {
            const sessionName = await getSessionName();
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(withSessionName(environment.signalRBase + '/notificationhub', sessionName), { accessTokenFactory: () => this.authService.jwtToken })
                .configureLogging(signalR.LogLevel.Information)
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: ctx => {
                        if (ctx.previousRetryCount < 5) { //this will retry 5 times over the course of 3.75sec
                            return ctx.previousRetryCount * 250;
                        }
                        if (ctx.previousRetryCount < 30) { //this will retry 25 times over 6 minutes
                            return 15000;
                        }
                        if (ctx.previousRetryCount > 300) {
                            return 3 * 60 * 1000; //3min
                        }
                        return 60 * 1000; //1 min
                    }
                })
                .build();
            this.connection.on('ReceiveNotification', this.receiveNotification.bind(this));
            this.connection.onclose(async () => {
                console.log('SignalR connection closed.');
            });
            // A reconnect (network blip, backgrounded phone) gets a brand new ConnectionId, and the
            // server only delivers to connections that joined the user's group through Connect() -
            // without re-invoking it the socket looks healthy but receives nothing. Anything sent
            // while the connection was down is announced through reconnected$ so its consumers can
            // re-fetch what they missed.
            this.connection.onreconnected(async () => {
                try {
                    await this.connection.invoke('Connect');
                    this.reconnectedSubject.next();
                } catch (err) {
                    console.log(err);
                }
            });

            this.startSignalR();
        }
    }

    private async startSignalR() {
        try {
            await this.connection.start();
            console.log('SignalR Connected.');
            this.connection.invoke('Connect');
            this.reconnectedSubject.next();
        } catch (err) {
            console.log(err);
            setTimeout(() => this.startSignalR(), 1000);
        }
    };

    private async receiveNotification(serverNotification: Signal): Promise<void> {
        if (serverNotification) {
            switch (serverNotification.SignalType) {
                case SignalType.Notification: serverNotification = new NotificationSignal(<NotificationSignal>serverNotification, this.authService.authenticatedUser$.getValue().Username, this.jsonMapperService.jsonEnricher());
                    break;
                case SignalType.Spotlight: serverNotification = new SpotlightSignal(<SpotlightSignal>serverNotification);
                    break;
                case SignalType.RandomEvent: serverNotification = new RandomEventNotification(<RandomEventNotification>serverNotification, this.jsonMapperService.jsonEnricher());
                    break;
                default: break;
            }
            this.latestNotificationSignal.next(serverNotification); //this observable triggers dispatch to the appropriate handlers
        }
    }

    public createToast(body: string, title: string, visibleDuration: number | null): void {
        this.addToast(new BasicToast(body, title, visibleDuration));
    }

    public addToast(toast: ToastNotification): void {
        toast.onClose = () => { toast.visible = false; this.removeToast(toast); };
        this.toastsState.set([...this.toastsState(), toast]);
    }

    private removeToast(toast: ToastNotification) {
        this.toastsState.set(this.toastsState().filter(t => t !== toast));
    }

    public addAnnouncement(announcement: AnnouncementNotification) {
        announcement.onClose = () => { this.removeAnnouncement(announcement); };
        this.announcementsState.set([...this.announcementsState(), announcement]);
    }

    private removeAnnouncement(announcement: AnnouncementNotification) {
        this.announcementsState.set(this.announcementsState().filter(a => a !== announcement));
    }

}


export abstract class UINotification {
    public title: string;
    public onClose: Function;
}

export class AnnouncementNotification extends UINotification {
    constructor(body: string, title: string, eventInformation: RandomEventNotification) {
        super();
        this.body = body;
        this.title = title;
        this.eventInformation = eventInformation;
    }
    public body: string;
    public eventInformation: RandomEventNotification;
}

export abstract class ToastNotification extends UINotification {
    public visibleDuration: number | null;
    public visible = true;
}

export class BasicToast extends ToastNotification {
    constructor(body: string, title: string, visibleDuration: number | null) {
        super();
        this.title = title;
        this.body = body;
        this.visibleDuration = visibleDuration;
    }
    public body: string;
}

export class AchievementToast extends ToastNotification {
    constructor(description: string, title: string, image: string, visibleDuration: number | null) {
        super();
        this.name = title;
        this.title = 'Achievement Unlocked!';
        this.body = description;
        this.image = image;
        this.visibleDuration = visibleDuration;
    }
    public body: string;
    public image: string;
    public name: string;
}

export class MessageToast extends ToastNotification {
    constructor(newMessage: string, senderUsername: string, channel: Channel, visibleDuration: number | null) {
        super();
        this.message = newMessage;
        this.senderUsername = senderUsername;
        this.channel = channel;
        this.visibleDuration = visibleDuration;

        this.title = `New message from ${channel.Name ? channel.Name : senderUsername}`;
    }
    public message: string;
    public senderUsername: string;
    public channel: Channel;
}

export class FriendshipToast extends ToastNotification {
    constructor(alertText: string, friendshipDetails: Friendship, visibleDuration: number | null, activeUser: User) {
        super();
        this.friendshipDetails = friendshipDetails;
        this.visibleDuration = visibleDuration;
        if (this.friendshipDetails.RecipientUsername.localeCompare(activeUser.Username) === 0) {

            if (this.friendshipDetails.Accepted) {
                this.visible = false;
                this.visibleDuration = 0; return;
            }
            this.message = `${this.friendshipDetails.SenderUsername} asked to be your friend!`;

        } else if (this.friendshipDetails.SenderUsername.localeCompare(activeUser.Username) === 0) {
            if (this.friendshipDetails.Accepted) {
                this.message = `${this.friendshipDetails.RecipientUsername} accepted your friend request!`;
            } else {
                this.visible = false;
                this.visibleDuration = 0; return;
            }
        }
        this.title = alertText;
    }
    public friendshipDetails: Friendship;
    public message: string;
    public channel: Channel;
}
